import {
  accentHex,
  DEFAULT_ACCENT_ID,
  isDefaultAccent,
} from '@ping/ui/src/preferences/accentPalette';

interface PreferencesState {
  theme: 'light' | 'dark' | 'system';
  accentId: string;
  bubblePaletteId: string;
  chatBackground: 'plain' | 'subtle' | 'gradient';
  fontScale: number;
  enterToSend: boolean;
  showReadReceipts: boolean;
  showTypingIndicators: boolean;
  notificationsEnabled: boolean;
  /** Show native OS notifications (browser Notification API) when Ping is
   *  backgrounded. Distinct from the in-app banner, which always shows when
   *  the window is focused on another chat. */
  osNotificationsEnabled: boolean;
  notificationPreview: 'full' | 'name' | 'none';
  inAppSounds: boolean;
  /** Slack-style: suppress alerts for the conversation you're actively
   *  viewing while the window is focused. */
  notifyOnlyWhenUnfocused: boolean;
  /** Do-Not-Disturb / quiet hours. When enabled, non-mention alerts are
   *  silenced between dndStart and dndEnd (local 24h "HH:mm"). */
  dndEnabled: boolean;
  dndStart: string;
  dndEnd: string;
  autoDownloadOnCellular: boolean;
}

type PreferencesListener = (state: PreferencesState) => void;

class PreferencesStore {
  private state: PreferencesState;
  private listeners: Set<PreferencesListener> = new Set();

  private static readonly STORAGE_KEY = 'com.ping.web.preferences';
  private static readonly DEFAULTS: PreferencesState = {
    // Dark-first: first-time visitors land on the new dark design that
    // matches the Figma auth screens (file gtBsWqJJcI6ZBuBv51RU35).
    // Users can switch to `'light'` or `'system'` from Settings.
    theme: 'dark',
    accentId: DEFAULT_ACCENT_ID,
    bubblePaletteId: 'default',
    chatBackground: 'plain',
    fontScale: 1.0,
    enterToSend: true,
    showReadReceipts: true,
    showTypingIndicators: true,
    notificationsEnabled: true,
    osNotificationsEnabled: true,
    notificationPreview: 'full',
    inAppSounds: true,
    notifyOnlyWhenUnfocused: true,
    dndEnabled: false,
    dndStart: '22:00',
    dndEnd: '08:00',
    autoDownloadOnCellular: false,
  };

  constructor() {
    this.state = this.loadState();
    this.applyTheme(this.state.theme);
    // Re-apply the persisted accent + zoom on boot so a reload restores the
    // user's appearance before first paint (the theme is applied above).
    this.applyAccent(this.state.accentId);
    this.applyChatBackground(this.state.chatBackground);
    this.applyFontScale(this.state.fontScale);
  }

  private loadState(): PreferencesState {
    try {
      const stored = localStorage.getItem(PreferencesStore.STORAGE_KEY);
      if (stored) {
        return { ...PreferencesStore.DEFAULTS, ...JSON.parse(stored) };
      }
    } catch (error) {
      console.error('Failed to load preferences:', error);
    }
    return { ...PreferencesStore.DEFAULTS };
  }

  private saveState(): void {
    try {
      localStorage.setItem(PreferencesStore.STORAGE_KEY, JSON.stringify(this.state));
    } catch (error) {
      console.error('Failed to save preferences:', error);
    }
  }

  private applyTheme(theme: PreferencesState['theme']): void {
    const effectiveTheme =
      theme === 'system'
        ? window.matchMedia('(prefers-color-scheme: dark)').matches
          ? 'dark'
          : 'light'
        : theme;
    // Three sources of truth — keep them in lockstep:
    //   1) `data-theme="dark"` drives the legacy CSS-var palette in
    //      apps/web/src/design-system/tokens.css.
    //   2) The literal `dark` class drives NativeWind's `dark:`
    //      utilities + the css-interop runtime (we run with
    //      `darkMode: 'class'` in tailwind.config.cjs).
    //   3) `color-scheme` lets the browser pick the matching
    //      scrollbar / form-control variant for the effective theme.
    const html = document.documentElement;
    html.setAttribute('data-theme', effectiveTheme);
    html.classList.toggle('dark', effectiveTheme === 'dark');
    html.style.colorScheme = effectiveTheme;
    this.bindSystemThemeListener(theme);
  }

  // When the user is on "system", track live OS light/dark changes so the
  // app follows the OS toggle without a reload. We keep at most one MQL
  // listener and detach it whenever the user picks an explicit theme — that
  // both honours the explicit choice and avoids leaking a listener that
  // re-applies "system" over their selection.
  private systemThemeMql: MediaQueryList | null = null;
  private systemThemeHandler: ((e: MediaQueryListEvent) => void) | null = null;
  private bindSystemThemeListener(theme: PreferencesState['theme']): void {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    // Always detach the previous listener first (idempotent + no leak).
    if (this.systemThemeMql && this.systemThemeHandler) {
      this.systemThemeMql.removeEventListener('change', this.systemThemeHandler);
      this.systemThemeMql = null;
      this.systemThemeHandler = null;
    }
    if (theme !== 'system') return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (): void => {
      // Re-derive only while still on "system" (defensive — the listener is
      // detached on any explicit pick, but a queued event could still fire).
      if (this.state.theme === 'system') this.applyTheme('system');
    };
    mql.addEventListener('change', handler);
    this.systemThemeMql = mql;
    this.systemThemeHandler = handler;
  }

  // Push the chosen accent into the runtime override: a non-default accent
  // sets `--ping-accent` + the `data-ping-accent` gate (the override
  // stylesheet only re-points the brand utilities under that attribute); the
  // default accent removes both so the shipped design tokens render as-is.
  private applyAccent(accentId: string): void {
    if (typeof document === 'undefined') return;
    const html = document.documentElement;
    if (isDefaultAccent(accentId)) {
      html.removeAttribute('data-ping-accent');
      html.style.removeProperty('--ping-accent');
      return;
    }
    html.style.setProperty('--ping-accent', accentHex(accentId));
    html.setAttribute('data-ping-accent', accentId);
  }

  // "Chat background" (Settings → Appearance). Reflects the choice onto
  // `html[data-chat-bg]`, which `chat-background.css` reads to paint the
  // chat thread backdrop. "plain" removes the attribute so the shipped
  // surface shows through (the default).
  private applyChatBackground(value: PreferencesState['chatBackground']): void {
    if (typeof document === 'undefined') return;
    const html = document.documentElement;
    if (value === 'plain') {
      html.removeAttribute('data-chat-bg');
    } else {
      html.setAttribute('data-chat-bg', value);
    }
  }

  // Entire-app zoom (Settings → Appearance → "Zoom scale"). We expose the
  // scale as the `--ping-zoom` custom property on <html>; `index.html` reads
  // it to `zoom: var(--ping-zoom)` the #root. No height compensation is needed:
  // the standardized `zoom` makes `#root { height: 100% }` fill the viewport at
  // any scale (a `calc(100% / zoom)` would over-compensate and leave a gap
  // above 100% / clip below — see the note in `index.html`). CSS `zoom` scales
  // text, spacing, and icons together — the "make everything bigger/smaller"
  // intent. A scale of 1 clears the var so `zoom` falls back to the inert 1.
  private applyFontScale(fontScale: number): void {
    if (typeof document === 'undefined') return;
    const html = document.documentElement;
    if (Math.abs(fontScale - 1) < 0.001) {
      html.style.removeProperty('--ping-zoom');
    } else {
      html.style.setProperty('--ping-zoom', String(fontScale));
    }
  }

  private notify(): void {
    this.listeners.forEach((listener) => listener(this.state));
  }

  /**
   * Apply a patch IMMUTABLY — assign a NEW state object so React's
   * `useState`/`Object.is` change-detection in `usePreferences` actually
   * re-renders. The previous in-place mutation (`this.state.x = v`) handed
   * subscribers the SAME object reference, so React bailed out of the render
   * and Settings toggles/selects looked frozen / showed stale values even
   * though the change was persisted. Mirrors the desktop controller.
   */
  private mutate(patch: Partial<PreferencesState>): void {
    this.state = { ...this.state, ...patch };
    this.saveState();
    this.notify();
  }

  subscribe(listener: PreferencesListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  // Return the live (immutable-per-change) state object directly so an
  // unchanged store yields a STABLE reference — `useMemo`/`React.memo`
  // consumers don't re-run when nothing changed, and a real change yields a
  // fresh reference that does trigger a re-render.
  getState(): PreferencesState {
    return this.state;
  }

  setTheme(theme: PreferencesState['theme']): void {
    this.applyTheme(theme);
    this.mutate({ theme });
  }

  setAccentId(accentId: string): void {
    this.applyAccent(accentId);
    this.mutate({ accentId });
  }

  setBubblePaletteId(bubblePaletteId: string): void {
    this.mutate({ bubblePaletteId });
  }

  setChatBackground(chatBackground: PreferencesState['chatBackground']): void {
    this.applyChatBackground(chatBackground);
    this.mutate({ chatBackground });
  }

  setFontScale(fontScale: number): void {
    const clamped = Math.max(0.85, Math.min(1.3, fontScale));
    this.applyFontScale(clamped);
    this.mutate({ fontScale: clamped });
  }

  setEnterToSend(enterToSend: boolean): void {
    this.mutate({ enterToSend });
  }

  setShowReadReceipts(showReadReceipts: boolean): void {
    this.mutate({ showReadReceipts });
  }

  setShowTypingIndicators(showTypingIndicators: boolean): void {
    this.mutate({ showTypingIndicators });
  }

  setNotificationsEnabled(notificationsEnabled: boolean): void {
    this.mutate({ notificationsEnabled });
  }

  setOsNotificationsEnabled(osNotificationsEnabled: boolean): void {
    this.mutate({ osNotificationsEnabled });
  }

  setNotificationPreview(notificationPreview: PreferencesState['notificationPreview']): void {
    this.mutate({ notificationPreview });
  }

  setInAppSounds(inAppSounds: boolean): void {
    this.mutate({ inAppSounds });
  }

  setNotifyOnlyWhenUnfocused(notifyOnlyWhenUnfocused: boolean): void {
    this.mutate({ notifyOnlyWhenUnfocused });
  }

  setDndEnabled(dndEnabled: boolean): void {
    this.mutate({ dndEnabled });
  }

  setDndWindow(dndStart: string, dndEnd: string): void {
    this.mutate({ dndStart, dndEnd });
  }

  setAutoDownloadOnCellular(autoDownloadOnCellular: boolean): void {
    this.mutate({ autoDownloadOnCellular });
  }

  resetToDefaults(): void {
    this.state = { ...PreferencesStore.DEFAULTS };
    this.applyTheme(this.state.theme);
    this.applyAccent(this.state.accentId);
    this.applyChatBackground(this.state.chatBackground);
    this.applyFontScale(this.state.fontScale);
    this.saveState();
    this.notify();
  }

  wipe(): void {
    localStorage.removeItem(PreferencesStore.STORAGE_KEY);
    this.state = { ...PreferencesStore.DEFAULTS };
    this.applyTheme(this.state.theme);
    this.applyAccent(this.state.accentId);
    this.applyChatBackground(this.state.chatBackground);
    this.applyFontScale(this.state.fontScale);
    this.notify();
  }
}

export const preferencesStore = new PreferencesStore();
export type { PreferencesState };
